import { api } from '@/api/client';

export interface ActivityLogEntry {
  ts: string;
  userId: string;
  userName: string;
  roles: string[];
  action: string;
  detail?: string;
  taskId?: number;
  projectId?: number;
  module?: string;
  meta?: Record<string, unknown>;
}

export async function fetchActivityLog(limit = 100, module?: string): Promise<{ entries: ActivityLogEntry[]; total: number }> {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (module) params.set('module', module);
  return api<{ entries: ActivityLogEntry[]; total: number }>(`/api/activity-log?${params}`);
}

/**
 * Display-safe projection returned by the project activity feed — a SUBSET of
 * ActivityLogEntry. The backend strips meta/userId/roles for this endpoint
 * (defence-in-depth), so only these fields are present.
 */
export interface ProjectActivityEntry {
  ts: string;
  action: string;
  detail?: string;
  taskId?: number;
  projectId?: number;
  module?: string;
  userName: string;
}

/**
 * Project-scoped activity feed for the dashboard mini-widget (Fase 7).
 * Scoped read (`project:activity:read`) — only entries carrying this project's
 * id surface (planning, onderaannemers PO, meerwerken, sharepoint saves/downloads).
 */
export async function fetchProjectActivity(projectId: number, limit = 5): Promise<{ entries: ProjectActivityEntry[] }> {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  return api<{ entries: ProjectActivityEntry[] }>(`/api/projects/${projectId}/activity?${params}`);
}

/* ---------------------------------------------------------------------------
 * Aggregated Odoo-chatter feed (dashboard "Recente activiteit"). Admin-only at
 * the API (`project:chatter:read`). Separate, additive endpoint — distinct from
 * the portal activity-log above. Server returns PLAIN TEXT only (no markup);
 * the frontend renders every field as React text, never as HTML.
 * ------------------------------------------------------------------------- */

export type ChatterSource = 'crm' | 'offerte' | 'project' | 'taak' | 'e-mail';
export type ChatterKind = 'comment' | 'email' | 'milestone';

export interface ProjectChatterEntry {
  id: string;
  ts: string;
  source: ChatterSource;
  kind: ChatterKind;
  action: string;
  detail?: string;
  userName?: string;
  direction?: 'inkomend' | 'uitgaand';
  snippet?: string;
  fullText?: string;
  taskId?: number;
  /** 'taak' rows only — the task's name (e.g. fase), used as the row headline. */
  taskName?: string;
  /** Grouped-summary rows only (e.g. a planning fase bundling N tasks) — the
   *  per-item lines (dates / names) shown in the click-to-expand list. */
  items?: string[];
}

/**
 * Aggregated, server-cleaned Odoo-chatter feed for the dashboard widget.
 * `limit` caps server-side (max 50); `refresh` busts the per-project cache.
 */
export async function fetchProjectChatter(
  projectId: number,
  opts?: { limit?: number; refresh?: boolean },
): Promise<{ entries: ProjectChatterEntry[] }> {
  const params = new URLSearchParams();
  if (opts?.limit) params.set('limit', String(opts.limit));
  if (opts?.refresh) params.set('refresh', 'true');
  const qs = params.toString();
  return api<{ entries: ProjectChatterEntry[] }>(`/api/projects/${projectId}/chatter${qs ? `?${qs}` : ''}`);
}
